import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';
import { AppFile, CollectionActivityCreate, CollectionActivityUpdate } from '@dill/dill-shared';
import { firebaseFileUploadHelper, getFileNameExtension } from '../../utils/helpers';

interface GetBuyerCollectionActivitiesParams {
  supplierId: string;
  buyerId: string;
  startDate?: Date;
  endDate?: Date;
}

export const getBuyerCollectionActivities = createAsyncThunk(
  'collectionActivities/getBuyerCollectionActivities',
  async (
    { supplierId, buyerId, startDate, endDate }: GetBuyerCollectionActivitiesParams,
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      // Build query params
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', startDate.toISOString());
      }
      if (endDate) {
        params.append('endDate', endDate.toISOString());
      }

      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionActivities/buyer/${buyerId}/getBuyerCollectionActivities${queryString}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierCollectionActivities = createAsyncThunk(
  'collectionActivities/getSupplierCollectionActivities',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionActivities/getSupplierCollectionActivities`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createCollectionActivity = createAsyncThunk(
  'collectionActivities/createCollectionActivity',
  async (
    {
      supplierId,
      activityData,
      noteAttachmentsFiles,
    }: {
      supplierId: string;
      activityData: CollectionActivityCreate;
      noteAttachmentsFiles?: File[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      // Upload note attachment files if provided
      const uploadedAttachments: AppFile[] = [];
      if (noteAttachmentsFiles && noteAttachmentsFiles.length > 0) {
        for (let index = 0; index < noteAttachmentsFiles.length; index++) {
          const element = noteAttachmentsFiles[index];
          const fileExtension = getFileNameExtension({
            fileName: element.name,
            isIncludePeriod: true,
          });
          const blobUrl = URL.createObjectURL(element);
          const fileUrl = await firebaseFileUploadHelper({
            blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-attachment-${
              index + 1
            }${fileExtension}`,
            storagePath: `collectionActivities/${supplierId}`,
          });
          if (fileUrl) {
            uploadedAttachments.push({
              name: element.name,
              url: fileUrl,
            });
          }
        }
      }

      // Merge uploaded attachments into noteDetails if present
      const finalActivityData = { ...activityData };
      if (finalActivityData.noteDetails && uploadedAttachments.length > 0) {
        finalActivityData.noteDetails = {
          ...finalActivityData.noteDetails,
          attachments: [
            ...(finalActivityData.noteDetails.attachments || []),
            ...uploadedAttachments,
          ],
        };
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionActivities/createCollectionActivity`,
        finalActivityData,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateCollectionActivity = createAsyncThunk(
  'collectionActivities/updateCollectionActivity',
  async (
    {
      supplierId,
      activityId,
      updateData,
      noteAttachmentsFiles,
      currentNoteAttachments,
    }: {
      supplierId: string;
      activityId: string;
      updateData: CollectionActivityUpdate;
      noteAttachmentsFiles?: File[];
      currentNoteAttachments?: AppFile[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      // Upload new note attachment files if provided
      const uploadedAttachments: AppFile[] = [];
      if (noteAttachmentsFiles && noteAttachmentsFiles.length > 0) {
        for (let index = 0; index < noteAttachmentsFiles.length; index++) {
          const element = noteAttachmentsFiles[index];
          const fileExtension = getFileNameExtension({
            fileName: element.name,
            isIncludePeriod: true,
          });
          const blobUrl = URL.createObjectURL(element);
          const fileUrl = await firebaseFileUploadHelper({
            blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-attachment-${
              index + 1
            }${fileExtension}`,
            storagePath: `collectionActivities/${supplierId}`,
          });
          if (fileUrl) {
            uploadedAttachments.push({
              name: element.name,
              url: fileUrl,
            });
          }
        }
      }

      // Merge uploaded + current attachments into noteDetails if present
      const finalUpdateData = { ...updateData };
      if (finalUpdateData.noteDetails) {
        const existingAttachments = currentNoteAttachments?.filter((item) => item.url !== '') || [];
        finalUpdateData.noteDetails = {
          ...finalUpdateData.noteDetails,
          attachments: [...existingAttachments, ...uploadedAttachments],
        };
      }

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionActivities/${activityId}/updateCollectionActivity`,
        finalUpdateData,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const deleteCollectionActivity = createAsyncThunk(
  'collectionActivities/deleteCollectionActivity',
  async (
    {
      supplierId,
      activityId,
    }: {
      supplierId: string;
      activityId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionActivities/${activityId}/deleteCollectionActivity`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

interface GetSupplierCollectionTasksParams {
  supplierId: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const getSupplierCollectionTasks = createAsyncThunk(
  'collectionActivities/getSupplierCollectionTasks',
  async (
    { supplierId, startDate, endDate, sortBy, sortOrder }: GetSupplierCollectionTasksParams,
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (sortBy) params.append('sortBy', sortBy);
      if (sortOrder) params.append('sortOrder', sortOrder);

      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionActivities/getSupplierCollectionTasks${queryString}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const regenerateDillRecommendedTasks = createAsyncThunk(
  'collectionActivities/regenerateDillRecommendedTasks',
  async (
    {
      supplierId,
      filterPrompt,
      prioritizationPrompt,
      useCustomPromptOnly,
    }: {
      supplierId: string;
      filterPrompt: string;
      prioritizationPrompt: string;
      useCustomPromptOnly: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<{ success: boolean; createdCount: number }>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionActivities/regenerateDillRecommendedTasks`,
        { filterPrompt, prioritizationPrompt, useCustomPromptOnly },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
